// Trimming a report down to the part the job detail page has not already
// rendered. JSX-free so Node's test runner can import it — it strips TS
// types but cannot transform JSX, so this cannot live in JobDetail.tsx.

/**
 * Trim the parts of a report the page has already rendered structurally.
 *
 * Every report opens with a machine-readable header — an H1 plus ~25
 * `**Field:** value` lines that parse-report.cjs reads to build the DB index.
 * Rendered raw it is a wall of English field names, six of which are routinely
 * empty (`DomainFit:` with nothing after it), and every value in it is already
 * on this page: the score, the six dimensions, the salary, the summary. Then
 * comes a summary-card section which the 岗位速览 card above duplicates a third
 * time — and which appears TWICE in the file, because writeNumberedReport
 * prepends a normalised copy without removing the model's own.
 *
 * What is left is the narrative the reader actually came for. If a report has
 * no section headings at all, nothing is stripped — better a noisy card than an
 * empty one.
 */
export function reportNarrative(md: string): string {
  const firstSection = md.search(/^##\s+/m);
  if (firstSection === -1) return md;
  let body = md.slice(firstSection);
  // Drop every summary-card section (heading through to the next heading).
  // Spanish because this project grew out of a Spanish-language system; the
  // model still emits that heading, so all three spellings are matched.
  // End-of-input is (?![\s\S]) — JS has no \Z, and writing one silently
  // means "a literal Z", which left a trailing summary section in place.
  body = body.replace(
    /^##\s+(Tarjeta de Resumen|Summary Card|Resumen del Rol)\s*$[\s\S]*?(?=^##\s+|(?![\s\S]))/gim,
    '',
  );
  const trimmed = body.trim();
  return trimmed.length > 0 ? trimmed : md;
}
